// =============================================================================
// GENERATED by scripts/build-shared.cjs from functions/src/shared/safetyRates.js
// DO NOT EDIT — edit the canonical file and re-run `node scripts/build-shared.cjs`.
// (TASK-9570: ONE OSHA TRIR / DART calculation.)
//
// Side-effect module: attaches `window.SafetyRates`. hr.js (HR → Safety →
// Injury Rates) reads it at CALL time.
//
// The body below is the canonical file's PORTABLE CORE, copied byte for byte.
// =============================================================================
(function () {
  'use strict';

  /** OSHA's base: 100 full-time workers × 40 h × 50 weeks. */
  const OSHA_RATE_BASE_HOURS = 200000;

  /** Form 300 outcome columns, most serious first. */
  const OSHA_OUTCOMES = ['death', 'days-away', 'restricted', 'other'];

  const OSHA_OUTCOME_LABELS = {
    'death': 'Death (column G)',
    'days-away': 'Days away from work (column H)',
    'restricted': 'Job transfer or restriction (column I)',
    'other': 'Other recordable case (column J)',
  };

  /** Outcomes that count toward DART (columns H + I). */
  const OSHA_DART_OUTCOMES = ['days-away', 'restricted'];

  /** A clock entry longer than this is flagged (still counted) — likely a forgotten clock-out. */
  const SAFETY_RATE_LONG_SHIFT_HOURS = 24;

  /**
   * cases × 200,000 ÷ hours. Returns null (never 0, never Infinity) when the
   * rate cannot be computed: no hours, negative inputs, non-numbers.
   */
  function oshaIncidenceRate(cases, hours) {
    const c = Number(cases);
    const h = Number(hours);
    if (!Number.isFinite(c) || !Number.isFinite(h) || c < 0 || h <= 0) return null;
    return (c * OSHA_RATE_BASE_HOURS) / h;
  }

  /** Two decimals, the way OSHA/BLS and prequal forms print a rate. null stays null. */
  function roundSafetyRate(rate) {
    if (rate === null || rate === undefined || !Number.isFinite(rate)) return null;
    return Math.round(rate * 100) / 100;
  }

  function _srValidOutcome(v) {
    return OSHA_OUTCOMES.indexOf(v) >= 0 ? v : null;
  }

  /**
   * How many Form 300 cases one incident is. Each injured EMPLOYEE is a
   * separate line on the log, so a recorded `oshaCaseCount` wins; without one
   * the incident counts as 1 and `explicit` is false.
   */
  function incidentOshaCaseCount(incident) {
    const n = Number(incident && incident.oshaCaseCount);
    if (Number.isInteger(n) && n >= 1 && n <= 100) return { count: n, explicit: true };
    return { count: 1, explicit: false };
  }

  /**
   * Period bounds as epoch ms, [startMs, endMs). Local time.
   *   { kind: 'year', year: 2026 }           → Jan 1 2026 .. Jan 1 2027 (the 300A year)
   *   { kind: 'rolling12', nowMs: <ms> }     → same calendar day 12 months back .. now
   * Returns null for anything else.
   */
  function safetyRatePeriod(spec) {
    const s = spec || {};
    if (s.kind === 'year') {
      const y = Number(s.year);
      if (!Number.isInteger(y) || y < 1970 || y > 9999) return null;
      return {
        kind: 'year',
        startMs: new Date(y, 0, 1).getTime(),
        endMs: new Date(y + 1, 0, 1).getTime(),
        label: 'Calendar year ' + y,
      };
    }
    if (s.kind === 'rolling12') {
      const now = Number(s.nowMs);
      if (!Number.isFinite(now)) return null;
      const end = new Date(now);
      const start = new Date(end.getFullYear(), end.getMonth() - 12, end.getDate());
      return { kind: 'rolling12', startMs: start.getTime(), endMs: now, label: 'Last 12 months' };
    }
    return null;
  }

  function _srInPeriod(ms, period) {
    return Number.isFinite(ms) && ms >= period.startMs && ms < period.endMs;
  }

  /**
   * Build TRIR + DART for one period.
   *
   * input = {
   *   period:       from safetyRatePeriod(),
   *   incidents:    [{ id, type, oshaRecordable, oshaOutcome, oshaCaseCount, peopleCount, dateMs }]
   *   clockEntries: [{ personId, clockInMs, hours, status }]   (hours = totalHours || hours)
   *   personClass:  { [personId]: 'employee' | '1099' | 'unclassified' }
   *   include1099:  bool  — count 1099 / owner-operator clock hours (only if you supervise them day to day)
   *   extraHours:   number | null — hours worked OFF the time clock (salaried, office). null = not answered.
   * }
   */
  function buildSafetyRates(input) {
    const inp = input || {};
    const period = inp.period;
    if (!period || !Number.isFinite(period.startMs) || !Number.isFinite(period.endMs)) {
      return { ok: false, error: 'period' };
    }

    // ---- cases ----------------------------------------------------------------
    const byOutcome = { 'death': 0, 'days-away': 0, 'restricted': 0, 'other': 0, 'unclassified': 0 };
    let totalCases = 0;
    const recordables = [];
    const noOutcome = [];
    const caseCountUnconfirmed = [];
    const undatedRecordable = [];
    const notAttested = [];
    const multiCase = [];   // >1 case on one incident: all share its one outcome
    (Array.isArray(inp.incidents) ? inp.incidents : []).forEach(function (inc) {
      if (!inc) return;
      const dated = Number.isFinite(inc.dateMs);
      if (inc.oshaRecordable === true && !dated) { undatedRecordable.push(inc.id); return; }
      if (!dated || !_srInPeriod(inc.dateMs, period)) return;
      if (inc.oshaRecordable !== true) {
        if (inc.type === 'injury' || inc.type === 'vehicle-accident') notAttested.push(inc.id);
        return;
      }
      const cc = incidentOshaCaseCount(inc);
      const outcome = _srValidOutcome(inc.oshaOutcome);
      totalCases += cc.count;
      byOutcome[outcome || 'unclassified'] += cc.count;
      if (!outcome) noOutcome.push(inc.id);
      if (!cc.explicit && Number(inc.peopleCount) > 1) caseCountUnconfirmed.push(inc.id);
      if (cc.count > 1) multiCase.push(inc.id);
      recordables.push({ id: inc.id, outcome: outcome, cases: cc.count, caseCountExplicit: cc.explicit, dateMs: inc.dateMs });
    });
    const dartKnown = byOutcome['days-away'] + byOutcome['restricted'];

    // ---- hours ----------------------------------------------------------------
    const cls = inp.personClass || {};
    const hoursBy = { employee: 0, '1099': 0, unclassified: 0 };
    let entriesCounted = 0;
    let longShifts = 0;
    const unclassifiedPeople = {};
    (Array.isArray(inp.clockEntries) ? inp.clockEntries : []).forEach(function (e) {
      if (!e || e.status !== 'completed') return;
      if (!_srInPeriod(e.clockInMs, period)) return;
      const h = Number(e.hours);
      if (!Number.isFinite(h) || h <= 0) return;
      let c = cls[e.personId];
      if (c !== 'employee' && c !== '1099') { c = 'unclassified'; unclassifiedPeople[e.personId || '?'] = true; }
      hoursBy[c] += h;
      entriesCounted += 1;
      if (h > SAFETY_RATE_LONG_SHIFT_HOURS) longShifts += 1;
    });
    const include1099 = inp.include1099 === true;
    const clockedHours = hoursBy.employee + hoursBy.unclassified + (include1099 ? hoursBy['1099'] : 0);
    const extraRaw = inp.extraHours;
    const extraAnswered = extraRaw !== null && extraRaw !== undefined && extraRaw !== '' && Number.isFinite(Number(extraRaw)) && Number(extraRaw) >= 0;
    const extraHours = extraAnswered ? Number(extraRaw) : 0;
    const totalHours = clockedHours + extraHours;

    // ---- flags (each one keeps `complete` false unless marked info) ------------
    const flags = [];
    if (!extraAnswered) flags.push({ code: 'hours-clock-only', blocking: true });
    if (totalHours <= 0) flags.push({ code: 'no-hours', blocking: true });
    if (noOutcome.length) flags.push({ code: 'outcome-missing', blocking: true, ids: noOutcome });
    if (caseCountUnconfirmed.length) flags.push({ code: 'case-count-unconfirmed', blocking: true, ids: caseCountUnconfirmed });
    if (undatedRecordable.length) flags.push({ code: 'recordable-undated', blocking: true, ids: undatedRecordable });
    const unclassifiedCount = Object.keys(unclassifiedPeople).length;
    // Blocking (COD r1): a person with no W-2 / 1099 setting may be a contractor OSHA
    // excludes — counting them silently would let a guessed denominator read as final.
    if (unclassifiedCount) flags.push({ code: 'workers-unclassified', blocking: true, count: unclassifiedCount, hours: hoursBy.unclassified });
    if (longShifts) flags.push({ code: 'long-shifts', blocking: false, count: longShifts });
    if (multiCase.length) flags.push({ code: 'multi-case-one-outcome', blocking: false, ids: multiCase });
    if (notAttested.length) flags.push({ code: 'not-attested', blocking: false, ids: notAttested });
    const complete = !flags.some(function (f) { return f.blocking; });

    const trir = oshaIncidenceRate(totalCases, totalHours);
    // DART with unknown outcomes is only a floor — say so instead of printing it as the rate.
    const dartIsFloor = noOutcome.length > 0;
    const dart = oshaIncidenceRate(dartKnown, totalHours);

    return {
      ok: true,
      period: period,
      base: OSHA_RATE_BASE_HOURS,
      cases: { total: totalCases, dart: dartKnown, byOutcome: byOutcome, recordables: recordables },
      hours: {
        total: totalHours,
        clocked: clockedHours,
        extra: extraHours,
        extraAnswered: extraAnswered,
        employee: hoursBy.employee,
        contractor: hoursBy['1099'],
        unclassified: hoursBy.unclassified,
        include1099: include1099,
        entries: entriesCounted,
      },
      trir: trir,
      trirRounded: roundSafetyRate(trir),
      dart: dart,
      dartRounded: roundSafetyRate(dart),
      dartIsFloor: dartIsFloor,
      complete: complete,
      flags: flags,
    };
  }

  window.SafetyRates = Object.freeze({
    BASE_HOURS: OSHA_RATE_BASE_HOURS,
    OUTCOMES: OSHA_OUTCOMES.slice(),
    OUTCOME_LABELS: Object.freeze(Object.assign({}, OSHA_OUTCOME_LABELS)),
    DART_OUTCOMES: OSHA_DART_OUTCOMES.slice(),
    LONG_SHIFT_HOURS: SAFETY_RATE_LONG_SHIFT_HOURS,
    rate: oshaIncidenceRate,
    round: roundSafetyRate,
    caseCount: incidentOshaCaseCount,
    period: safetyRatePeriod,
    build: buildSafetyRates
  });
})();
